import {
  AppModuleCode,
  CHANGE_CALENDAR_TIMEZONE,
  CHANGE_RISK_LABELS,
  ChangeRisk,
  PermissionCode,
  TicketType,
  changeWindowOverlapsBerlinDay,
  type ChangeCalendarItem,
  type TicketListQuery,
} from '@gb-ticket/shared-types';
import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useModule } from '@/features/auth/use-module';
import {
  berlinDateParts,
  selectThisWeekChanges,
} from '@/products/helpdesk/features/changes/change-upcoming';
import { ChangeCalendarDayCell } from '@/products/helpdesk/features/changes/change-calendar-day-cell';
import {
  ChangeApprovalBadge,
  ChangeRiskBadge,
  StatusBadge,
} from '@/products/helpdesk/features/tickets/ticket-badges';
import { useChangeCalendar, useTickets } from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';
import { formatDateTimeInZone } from '@/lib/format';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

type ChangesView = 'kalender' | 'liste' | 'ohne-fenster';
type FreigabeFilter = 'ja' | 'nein' | 'wartend' | '';

const VIEW_VALUES: readonly ChangesView[] = ['kalender', 'liste', 'ohne-fenster'];

function parseView(value: string | null): ChangesView {
  if (value && VIEW_VALUES.includes(value as ChangesView)) {
    return value as ChangesView;
  }
  return 'kalender';
}

function parseFreigabe(value: string | null): FreigabeFilter {
  if (value === 'ja' || value === 'nein' || value === 'wartend') {
    return value;
  }
  return '';
}

function freigabeToApproval(value: FreigabeFilter): TicketListQuery['changeApproval'] {
  if (value === 'ja') {
    return 'approved';
  }
  if (value === 'nein') {
    return 'rejected';
  }
  if (value === 'wartend') {
    return 'pending';
  }
  return undefined;
}

function parseRisiko(values: string[]): ChangeRisk[] | undefined {
  const allowed = new Set<string>(Object.values(ChangeRisk));
  const risks = values.filter((value): value is ChangeRisk => allowed.has(value));
  return risks.length > 0 ? risks : undefined;
}

function monthLabel(year: number, month: number): string {
  return new Intl.DateTimeFormat('de-DE', {
    month: 'long',
    year: 'numeric',
    timeZone: CHANGE_CALENDAR_TIMEZONE,
  }).format(new Date(Date.UTC(year, month - 1, 15, 12)));
}

function ymd(year: number, month: number, day: number): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

function berlinWeekdayMondayFirst(year: number, month: number, day: number): number {
  const noonUtcGuess = new Date(Date.UTC(year, month - 1, day, 12));
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: CHANGE_CALENDAR_TIMEZONE,
    weekday: 'short',
  }).format(noonUtcGuess);
  const map: Record<string, number> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
  };
  return map[weekday] ?? 0;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function formatChangeRange(start: string, end: string): string {
  return `${formatDateTimeInZone(start, CHANGE_CALENDAR_TIMEZONE)}–${formatDateTimeInZone(end, CHANGE_CALENDAR_TIMEZONE)}`;
}

function formatCalendarItemRange(item: ChangeCalendarItem): string {
  return formatChangeRange(item.scheduledStart, item.scheduledEnd);
}

export function ChangeCalendarPage() {
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const changesOn = useModule(AppModuleCode.CHANGES);
  const canCreate = hasPermission(me.data, PermissionCode.TICKETS_CREATE);
  const now = berlinDateParts();
  const year = Number(params.get('jahr')) || now.year;
  const month = Number(params.get('monat')) || now.month;
  const highlightDay = Number(params.get('tag')) || null;
  const view = parseView(params.get('ansicht'));
  const freigabe = parseFreigabe(params.get('freigabe'));
  const risiko = parseRisiko(params.getAll('risiko'));

  const calendar = useChangeCalendar({ year, month });

  const listQuery = useMemo<TicketListQuery>(
    () => ({
      page: 1,
      pageSize: 50,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
      type: [TicketType.CHANGE],
      changeWindow: view === 'ohne-fenster' ? 'none' : undefined,
      changeRisk: view === 'liste' ? risiko : undefined,
      changeApproval: view === 'liste' ? freigabeToApproval(freigabe) : undefined,
    }),
    [view, risiko, freigabe],
  );
  const list = useTickets(listQuery, { enabled: view !== 'kalender' });

  const cells = useMemo(() => {
    const startWeekday = berlinWeekdayMondayFirst(year, month, 1);
    const days = daysInMonth(year, month);
    const total = Math.ceil((startWeekday + days) / 7) * 7;
    return Array.from({ length: total }, (_, index) => {
      const day = index - startWeekday + 1;
      if (day < 1 || day > days) {
        return { day: null as number | null };
      }
      return { day };
    });
  }, [year, month]);

  function patchParams(patch: Record<string, string | string[] | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      next.delete(key);
      if (value === null || value === '') {
        continue;
      }
      if (Array.isArray(value)) {
        for (const entry of value) {
          next.append(key, entry);
        }
      } else {
        next.set(key, value);
      }
    }
    if (!next.get('jahr')) {
      next.set('jahr', String(year));
    }
    if (!next.get('monat')) {
      next.set('monat', String(month));
    }
    setParams(next);
  }

  function setView(nextView: ChangesView) {
    patchParams({
      ansicht: nextView === 'kalender' ? null : nextView,
      freigabe: nextView === 'liste' ? freigabe || null : null,
      risiko: nextView === 'liste' ? (risiko ?? null) : null,
    });
  }

  function go(offset: number) {
    const next = new Date(Date.UTC(year, month - 1 + offset, 1));
    patchParams({
      jahr: String(next.getUTCFullYear()),
      monat: String(next.getUTCMonth() + 1),
      tag: null,
      ansicht: view === 'kalender' ? null : view,
    });
  }

  function goToday() {
    const today = berlinDateParts();
    patchParams({
      jahr: String(today.year),
      monat: String(today.month),
      tag: String(today.day),
      ansicht: null,
    });
    requestAnimationFrame(() => {
      document
        .getElementById(`tag-${today.day}`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  function toggleRisiko(code: ChangeRisk) {
    const current = new Set(risiko ?? []);
    if (current.has(code)) {
      current.delete(code);
    } else {
      current.add(code);
    }
    patchParams({
      ansicht: 'liste',
      risiko: [...current],
      freigabe: freigabe || null,
    });
  }

  function setFreigabe(next: FreigabeFilter) {
    patchParams({
      ansicht: 'liste',
      freigabe: next || null,
      risiko: risiko ?? null,
    });
  }

  const calendarItems = calendar.data?.items ?? [];
  const showEmptyMonth =
    Boolean(calendar.data) && !calendar.isPending && calendarItems.length === 0;
  const listItems = list.data?.items ?? [];
  const weekItems = useMemo(() => selectThisWeekChanges(calendarItems), [calendarItems]);
  const todayParts = berlinDateParts();
  const isViewingTodayMonth = year === todayParts.year && month === todayParts.month;

  useEffect(() => {
    if (view !== 'kalender' || !highlightDay || !calendar.data) {
      return;
    }
    const node = document.getElementById(`tag-${highlightDay}`);
    node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [view, year, month, highlightDay, calendar.data]);

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Änderungen</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Kalender, Liste und Changes ohne Zeitfenster. Zeiten: Europe/Berlin.
          </p>
        </div>
        {changesOn && canCreate ? (
          <Button asChild>
            <Link to="/helpdesk/tickets/neu?typ=CHANGE">Änderung anlegen</Link>
          </Button>
        ) : null}
      </div>

      <Tabs value={view} onValueChange={(value) => setView(parseView(value))}>
        <TabsList aria-label="Änderungsansichten">
          <TabsTrigger value="kalender">Kalender</TabsTrigger>
          <TabsTrigger value="liste">Liste</TabsTrigger>
          <TabsTrigger value="ohne-fenster">Ohne Fenster</TabsTrigger>
        </TabsList>

        <TabsContent value="kalender" className="space-y-6">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={goToday}>
              Heute
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => go(-1)}
              aria-label="Vorheriger Monat"
            >
              <ChevronLeftIcon />
            </Button>
            <p className="min-w-40 text-center font-medium capitalize">{monthLabel(year, month)}</p>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => go(1)}
              aria-label="Nächster Monat"
            >
              <ChevronRightIcon />
            </Button>
          </div>

          {calendar.isPending && (
            <p className="flex items-center gap-2 text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
              Kalender wird geladen …
            </p>
          )}
          {calendar.isError && (
            <p className="text-sm text-destructive">
              {calendar.error instanceof ApiRequestError
                ? calendar.error.message
                : 'Der Kalender konnte nicht geladen werden.'}
            </p>
          )}

          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border bg-border">
            {WEEKDAYS.map((label) => (
              <div key={label} className="bg-muted px-2 py-2 text-center text-xs font-medium">
                {label}
              </div>
            ))}
            {cells.map((cell, index) => {
              if (!cell.day) {
                return <div key={`pad-${index}`} className="min-h-28 bg-muted/40 p-2" />;
              }
              const dayItems = calendar.data
                ? calendarItems.filter((item) =>
                    changeWindowOverlapsBerlinDay(
                      item.scheduledStart,
                      item.scheduledEnd,
                      year,
                      month,
                      cell.day!,
                    ),
                  )
                : [];
              const isToday = isViewingTodayMonth && cell.day === todayParts.day;
              return (
                <ChangeCalendarDayCell
                  key={ymd(year, month, cell.day)}
                  day={cell.day}
                  items={dayItems}
                  isToday={isToday}
                  formatRange={formatCalendarItemRange}
                />
              );
            })}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Diese Woche</CardTitle>
              <CardDescription>
                Geplante Changes in der aktuellen Kalenderwoche (Mo–So, Europe/Berlin)
                {!isViewingTodayMonth
                  ? ' — nur Einträge, die auch im angezeigten Monat liegen.'
                  : '.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {weekItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Changes in dieser Woche.</p>
              ) : (
                weekItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col gap-1 border-b pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <Link
                        to={`/helpdesk/tickets/${item.number}`}
                        className="font-medium text-primary hover:underline"
                      >
                        #{item.number} · {item.subject}
                      </Link>
                      <p className="text-sm text-muted-foreground">
                        {formatCalendarItemRange(item)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={item.status} />
                      <ChangeApprovalBadge status={item.approved ? 'APPROVED' : 'PENDING'} />
                      <ChangeRiskBadge risk={item.risk} />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {showEmptyMonth && (
            <Card>
              <CardHeader>
                <CardTitle>Keine geplanten Änderungen in diesem Monat</CardTitle>
                <CardDescription>
                  Es gibt keine Changes mit Zeitfenster für {monthLabel(year, month)}. Nutzen Sie
                  den Tab „Ohne Fenster“, um ungeplante Changes zu finden.
                </CardDescription>
              </CardHeader>
              {changesOn && canCreate ? (
                <CardContent>
                  <Button asChild variant="outline">
                    <Link to="/helpdesk/tickets/neu?typ=CHANGE">Änderung anlegen</Link>
                  </Button>
                </CardContent>
              ) : null}
            </Card>
          )}

          {calendarItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Alle geplanten Changes in diesem Monat</CardTitle>
                <CardDescription>{calendarItems.length} Einträge mit Zeitfenster.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {calendarItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col gap-1 border-b pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <Link
                        to={`/helpdesk/tickets/${item.number}`}
                        className="font-medium text-primary hover:underline"
                      >
                        #{item.number} · {item.subject}
                      </Link>
                      <p className="text-sm text-muted-foreground">
                        {formatCalendarItemRange(item)}
                        {item.affectedServices.length > 0
                          ? ` · ${item.affectedServices.join(', ')}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={item.status} />
                      <ChangeApprovalBadge status={item.approved ? 'APPROVED' : 'PENDING'} />
                      <ChangeRiskBadge risk={item.risk} />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="liste" className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex flex-wrap gap-2">
              <span className="self-center text-sm text-muted-foreground">Risiko:</span>
              {Object.values(ChangeRisk).map((code) => {
                const active = risiko?.includes(code);
                return (
                  <Button
                    key={code}
                    type="button"
                    size="sm"
                    variant={active ? 'default' : 'outline'}
                    onClick={() => toggleRisiko(code)}
                  >
                    {CHANGE_RISK_LABELS[code]}
                  </Button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="self-center text-sm text-muted-foreground">Freigabe:</span>
              {(
                [
                  ['', 'Alle'],
                  ['ja', 'Ja'],
                  ['wartend', 'Wartend'],
                  ['nein', 'Nein'],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value || 'all'}
                  type="button"
                  size="sm"
                  variant={freigabe === value ? 'default' : 'outline'}
                  onClick={() => setFreigabe(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          <ChangeTicketList
            pending={list.isPending}
            error={list.error}
            isError={list.isError}
            items={listItems}
            emptyLabel="Keine Changes für diese Filter."
            total={list.data?.total}
          />
        </TabsContent>

        <TabsContent value="ohne-fenster" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Changes ohne Start/Ende erscheinen nicht im Kalender. Setzen Sie das Zeitfenster auf der
            Ticketkarte.
          </p>
          <ChangeTicketList
            pending={list.isPending}
            error={list.error}
            isError={list.isError}
            items={listItems}
            emptyLabel="Alle Changes haben ein Zeitfenster."
            total={list.data?.total}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}

function ChangeTicketList({
  pending,
  isError,
  error,
  items,
  emptyLabel,
  total,
}: {
  pending: boolean;
  isError: boolean;
  error: unknown;
  items: NonNullable<ReturnType<typeof useTickets>['data']>['items'];
  emptyLabel: string;
  total?: number;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        {pending && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Wird geladen …
          </p>
        )}
        {isError && (
          <p className="text-sm text-destructive">
            {error instanceof ApiRequestError
              ? error.message
              : 'Die Liste konnte nicht geladen werden.'}
          </p>
        )}
        {!pending && !isError && items.length === 0 && (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        )}
        {items.map((ticket) => (
          <div
            key={ticket.id}
            className="flex flex-col gap-1 border-b pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <Link
                to={`/helpdesk/tickets/${ticket.number}`}
                className="font-medium text-primary hover:underline"
              >
                #{ticket.number} · {ticket.subject}
              </Link>
              <p className="text-sm text-muted-foreground">
                {ticket.changeScheduledStart && ticket.changeScheduledEnd
                  ? formatChangeRange(ticket.changeScheduledStart, ticket.changeScheduledEnd)
                  : 'Kein Zeitfenster gesetzt'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={ticket.status} />
              <ChangeApprovalBadge status={ticket.approvalStatus} />
              {ticket.changeRisk ? <ChangeRiskBadge risk={ticket.changeRisk} /> : null}
            </div>
          </div>
        ))}
        {total !== undefined && total > items.length ? (
          <p className="text-xs text-muted-foreground">
            Zeige {items.length} von {total}.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
